import { router } from "@inertiajs/react";
import { Button } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import type { DataTableSortStatus } from "mantine-datatable";
import { useState } from "react";
import { SchoolsTable } from "../../components/school/SchoolsTable";
import { showDeleteModal } from "../../helpers/modal";
import { useYearContext } from "../../hooks/useYearContext";
import { ContentShell } from "../../layout/ContentShell";
import type { Pagination } from "../../models/pagination";
import { getSchoolTranslation, type School } from "../../models/school";
import { edit_school_path, new_school_path, school_path, schools_path } from "../../routes";

type SchoolsProps = {
  schools: School[];
  filter: {
    sort_key?: "bms" | "title" | "short";
    sort_direction?: "asc" | "desc";
  };
  pagination: Pagination;
};

export default function Schools({ schools, filter, pagination }: SchoolsProps) {
  const { pathWithYearContext } = useYearContext();
  const [isFetching, setIsFetching] = useState(false);

  const sortStatus: DataTableSortStatus<School> = {
    columnAccessor: filter?.sort_key || "",
    direction: filter?.sort_direction || "asc",
  };

  const sortChange = (sortStatus: DataTableSortStatus<School>) => handlePageUpdate(sortStatus, pagination.page);
  const onPageChange = (page: number) => handlePageUpdate(sortStatus, page);

  const handlePageUpdate = (sortStatus: DataTableSortStatus<School>, page: number) => {
    setIsFetching(true);
    router.get(
      pathWithYearContext(schools_path),
      {
        filter: {
          sort_key: sortStatus.columnAccessor,
          sort_direction: sortStatus.direction,
        },
        page,
      },
      {
        onFinish: () => setIsFetching(false),
      },
    );
  };

  const onEdit = (school: School) => router.get(pathWithYearContext(edit_school_path, school.id));
  const onNew = () => router.get(pathWithYearContext(new_school_path));
  const onDelete = (school: School) =>
    showDeleteModal({
      title: `Schule ${getSchoolTranslation(school)}`,
      article: "die",
      onConfirm: () => router.delete(pathWithYearContext(school_path, school.id)),
    });

  return (
    <ContentShell
      title="Schulen"
      actions={
        <Button leftSection={<IconPlus />} variant="light" onClick={onNew}>
          Neu
        </Button>
      }
    >
      <SchoolsTable
        onEdit={onEdit}
        onDelete={onDelete}
        schools={schools}
        sortStatus={sortStatus}
        onSortStatusChange={sortChange}
        fetching={isFetching}
        pagination={pagination}
        onPageChange={onPageChange}
      />
    </ContentShell>
  );
}
